import { useQuery } from '@tanstack/react-query';
import { exchanges, health } from '@/api/endpoints';
import type { HealthComponent } from '@/api/types';
import { Notice } from '@/components/Notice';
import { formatTime, relativeAge } from '@/lib/format';
import { deriveMode } from '@/lib/mode';
import { useRealtime } from '@/stores/realtime';

const STATUS_TONE: Record<string, 'info' | 'warn' | 'error'> = {
  HEALTHY: 'info',
  WARNING: 'warn',
  UNKNOWN: 'warn',
  CRITICAL: 'error',
  OFFLINE: 'error',
};

const STATUS_COLOUR: Record<string, string> = {
  HEALTHY: 'var(--gain)',
  WARNING: 'var(--warn)',
  UNKNOWN: 'var(--warn)',
};

const SERVICE_NAMES: Record<string, string> = {
  database: 'Database',
  redis: 'Redis',
  migrations: 'Migrations',
  scheduler: 'Scheduler',
};

/**
 * What a service's status means. The migrations row has no timing of its own
 * in older API versions, and the page printed "undefined ms" for it.
 */
export function serviceDetail(component: HealthComponent): string {
  if (component.detail) return component.detail;
  if (typeof component.latency_ms === 'number') return `${component.latency_ms} ms`;
  if (component.revision) return component.revision;
  return '—';
}

export function DashboardPage() {
  const events = useRealtime((s) => s.events);
  const resyncNeeded = useRealtime((s) => s.resyncNeeded);

  const accounts = useQuery({ queryKey: ['exchange-accounts'], queryFn: exchanges.accounts });
  const systemHealth = useQuery({
    queryKey: ['health'],
    queryFn: health.detailed,
    refetchInterval: 15_000,
  });

  const summary = deriveMode(accounts.data);

  return (
    <>
      <div className="page-head">
        <h1>Overview</h1>
        <p>
          What the system is doing now: which accounts are trading, whether every service is
          running, and events as they happen. Positions, orders and profit are on their own
          screens.
        </p>
      </div>

      {resyncNeeded && (
        <Notice tone="warn">
          Some realtime events were missed. Refreshing the figures from the server…
        </Notice>
      )}

      <div className="grid-2">
        <section className="panel">
          <div className="panel-head">
            <h2>Accounts</h2>
          </div>
          <div className="panel-body" style={{ display: 'flex', gap: 'var(--space-6)' }}>
            <div className="stat">
              <span className="stat-value num">{summary.liveAccounts}</span>
              <span className="stat-label">Live enabled</span>
            </div>
            <div className="stat">
              <span className="stat-value num">{summary.simulatedAccounts}</span>
              <span className="stat-label">Simulated enabled</span>
            </div>
            <div className="stat">
              <span className="stat-value num">{summary.tradingAccounts}</span>
              <span className="stat-label">Currently trading</span>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Services</h2>
          </div>
          <div className="panel-body flush">
            {systemHealth.isError && <div className="empty">Health check unavailable.</div>}
            {systemHealth.data && (
              <table>
                <tbody>
                  {Object.entries(systemHealth.data.components).map(([name, component]) => {
                    const colour = STATUS_COLOUR[component.status] ?? 'var(--critical)';
                    return (
                      <tr key={name} data-testid={`service-${name}`}>
                        <td>{SERVICE_NAMES[name] ?? name}</td>
                        <td>
                          <span
                            className="badge"
                            data-tone={STATUS_TONE[component.status] ?? 'info'}
                            style={{ borderColor: colour, color: colour }}
                          >
                            {component.status.toLowerCase()}
                          </span>
                        </td>
                        <td className="right num">{serviceDetail(component)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h2>Live events</h2>
          <span className="hint">Newest first · {events.length} received this session</span>
        </div>
        <div className="panel-body flush">
          {events.length === 0 ? (
            <div className="empty">
              No events yet. Anything the system does while you are signed in appears here as it
              happens.
            </div>
          ) : (
            <div className="feed">
              {events.map((event) => (
                <div className="feed-row" key={`${event.channel}-${event.seq}`}>
                  <span className="feed-time num">{formatTime(event.ts)}</span>
                  <span className="feed-event">{event.event}</span>
                  <span className="feed-data num">{JSON.stringify(event.data)}</span>
                  <span className="badge" data-mode={event.simulated ? 'PAPER' : 'LIVE'}>
                    {event.simulated ? 'simulated' : 'live'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>Exchange accounts</h2>
        </div>
        <div className="panel-body flush">
          {(accounts.data ?? []).length === 0 ? (
            <div className="empty">No accounts yet. Add one under Exchanges.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Exchange</th>
                  <th>Label</th>
                  <th>Mode</th>
                  <th>State</th>
                  <th>Credentials</th>
                  <th className="right">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {(accounts.data ?? []).map((account) => (
                  <tr key={account.id}>
                    <td>{account.exchange_code}</td>
                    <td>{account.label}</td>
                    <td>
                      <span className="badge" data-mode={account.mode === 'LIVE' ? 'LIVE' : 'PAPER'}>
                        {account.mode.toLowerCase()}
                      </span>
                    </td>
                    <td>{account.trading_state.toLowerCase().replace('_', ' ')}</td>
                    <td className="num">
                      {account.credentials?.api_key_masked ?? (account.mode === 'PAPER' ? 'not needed' : 'none')}
                    </td>
                    <td className="right">{relativeAge(account.last_sync_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}
